﻿import { useEffect, useState, useMemo } from 'react'
import { toast } from 'react-toastify'
import { Box, Button, Input, Modal, Typography } from 'components/atomic'

import { useAuth } from 'store/auth/hooks'
import { useTournament } from 'store/tournament/hooks'
import { MatchWinnerRequestParams } from 'store/tournament/types'
import { useParams } from 'react-router-dom'
import { useAppSelector } from 'store/store'

import { Participant } from 'types/app'

// Confirmation status type (matches backend)
type ConfirmationStatus =
  | 'pending'
  | 'team1_reported'
  | 'team2_reported'
  | 'both_reported'
  | 'disputed'
  | 'confirmed'

export const SetMatchWinnerModal = () => {
  const { user } = useAuth()
  const { matchWinnerModal, closeMatchWinnerModal, setMatchWinner, getTournament } =
    useTournament()
  const { tournamentId: routeTournamentId } = useParams<{ tournamentId: string }>()

  // Get tournament info to check organizer and team captains
  const tournamentInfo = useAppSelector(
    (state: { tournament: { tournament: any } }) => state.tournament.tournament,
  )

  const {
    isOpen,
    tournamentId,
    matchIndex,
    roundIndex,
    pending,
    fullfiled,
    participants: participantsState,
    bracketType,
    // New fields for confirmation flow
    confirmationStatus,
    team1Reported,
    team2Reported,
  } = matchWinnerModal

  const [participants, setParticipants] = useState<Participant[]>([])
  const [images, setImages] = useState<string[]>([])
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0)
  const [myTeamScore, setMyTeamScore] = useState<number>(0)
  const [opponentScore, setOpponentScore] = useState<number>(0)

  // Determine user role
  const userId = user?._id?.toString()
  const isAdmin = user?.user_type === 'admin'
  const isOrganizer = tournamentInfo?.tournamentOrganizer?.toString() === userId

  // Find which team the user is captain of (if any)
  const userTeamInfo = useMemo(() => {
    if (!participantsState || participantsState.length < 2) return null

    const team1Id = participantsState[0]?.teamId
    const team2Id = participantsState[1]?.teamId

    // Check regular teams
    const team1 = tournamentInfo?.teams?.find((t: any) =>
      t.teamId?.toString() === team1Id || t._id?.toString() === team1Id
    )
    const team2 = tournamentInfo?.teams?.find((t: any) =>
      t.teamId?.toString() === team2Id || t._id?.toString() === team2Id
    )

    // Check draft teams for captain_pick tournaments
    const draftTeam1 = tournamentInfo?.draft_teams?.find((t: any) =>
      t.teamId?.toString() === team1Id || t._id?.toString() === team1Id
    )
    const draftTeam2 = tournamentInfo?.draft_teams?.find((t: any) =>
      t.teamId?.toString() === team2Id || t._id?.toString() === team2Id
    )

    const isTeam1Captain = (team1?.captain?.toString() === userId) ||
                          (team1?.captain?._id?.toString() === userId) ||
                          (draftTeam1?.captain?.toString() === userId) ||
                          (draftTeam1?.captain?._id?.toString() === userId)
    const isTeam2Captain = (team2?.captain?.toString() === userId) ||
                          (team2?.captain?._id?.toString() === userId) ||
                          (draftTeam2?.captain?.toString() === userId) ||
                          (draftTeam2?.captain?._id?.toString() === userId)

    if (isTeam1Captain) {
      return {
        isCaptain: true,
        teamIndex: 0,
        teamId: team1Id,
        teamName: participantsState[0]?.name,
        opponentName: participantsState[1]?.name
      }
    }
    if (isTeam2Captain) {
      return {
        isCaptain: true,
        teamIndex: 1,
        teamId: team2Id,
        teamName: participantsState[1]?.name,
        opponentName: participantsState[0]?.name
      }
    }

    return { isCaptain: false, teamIndex: -1, teamId: null, teamName: null, opponentName: null }
  }, [participantsState, tournamentInfo, userId])

  const canDirectlyConfirm = isAdmin || isOrganizer || tournamentInfo?.canManageTournament
  const isCaptain = userTeamInfo?.isCaptain || false

  useEffect(
    () => setParticipants(participantsState ? [...participantsState] : []),
    [participantsState],
  )

  // Move the useEffect for closing modal here, before any conditional returns
  useEffect(() => {
    if (fullfiled) {
      // Show success message
      const message = canDirectlyConfirm
        ? 'Match result confirmed!'
        : 'Score submitted! Waiting for opponent or admin confirmation.'
      toast.success(message, {
        position: 'top-right',
        autoClose: 2000,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: false,
      })

      // Just close the modal - the state is already updated in Redux
      closeMatchWinnerModal()
    }
  }, [fullfiled, closeMatchWinnerModal, canDirectlyConfirm])

  const showToastForError = (message: string) => {
    toast.error(message, {
      position: 'top-right',
      autoClose: 3000,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: false,
    })
  }

  const validateWinner = () => {
    setParticipants((ps) =>
      ps.map((p, i, arr) => ({
        ...p,
        isWinner: p.score === Math.max(...arr.map((a) => a.score)),
      })),
    )
  }

  if (
    matchIndex == undefined ||
    roundIndex == undefined ||
    participantsState == undefined
  ) {
    return <></>
  }

  const reset = () => {
    setParticipants([...participantsState])
    setMyTeamScore(0)
    setOpponentScore(0)
  }

  const changeScore = (teamId: string, score: number) => {
    setParticipants((ps) =>
      ps.map((ps) => ({
        ...ps,
        score: ps.teamId === teamId ? score : ps.score,
      })),
    )
  }

  // Admin/Organizer save - directly confirms result
  const saveAsAdmin = () => {
    if (participants[0].score === participants[1].score) {
      showToastForError('Both teams cannot have same score')
      return
    } else if (participants[0].score <= 0 && participants[1].score <= 0) {
      showToastForError('Score should be greater than 0')
      return
    }

    const data = {
      tournamentId,
      roundIndex,
      matchIndex,
      participants: participants,
      bracketType,
    }
    setMatchWinner(data as MatchWinnerRequestParams)
  }

  // Captain save - submits their score for confirmation
  const saveAsCaptain = () => {
    if (myTeamScore <= 0 && opponentScore <= 0) {
      showToastForError('Please enter valid scores')
      return
    }
    if (myTeamScore === opponentScore) {
      showToastForError('Scores cannot be tied')
      return
    }

    // Build participants with the captain's reported scores
    const updatedParticipants = participants.map((p, idx) => {
      if (userTeamInfo?.teamIndex === idx) {
        return { ...p, score: myTeamScore, isWinner: myTeamScore > opponentScore }
      } else {
        return { ...p, score: opponentScore, isWinner: opponentScore > myTeamScore }
      }
    })

    const data = {
      tournamentId,
      roundIndex,
      matchIndex,
      participants: updatedParticipants,
      bracketType,
      // Flag to indicate this is a captain submission
      captainSubmission: true,
      reportingTeamId: userTeamInfo?.teamId,
    }
    setMatchWinner(data as MatchWinnerRequestParams)
  }

  // Get confirmation status display
  const getConfirmationStatusDisplay = () => {
    if (!confirmationStatus || confirmationStatus === 'pending') return null

    switch (confirmationStatus) {
      case 'team1_reported':
        return (
          <div className="bg-yellow-500/20 border border-yellow-500 rounded-lg p-3 mb-4">
            <Typography className="text-yellow-500 text-sm">
              â³ {participantsState[0]?.name} has reported: {team1Reported?.score} - {team1Reported?.opponentScore}
            </Typography>
            <Typography className="text-yellow-400 text-xs mt-1">
              Waiting for {participantsState[1]?.name} to confirm or admin to verify
            </Typography>
          </div>
        )
      case 'team2_reported':
        return (
          <div className="bg-yellow-500/20 border border-yellow-500 rounded-lg p-3 mb-4">
            <Typography className="text-yellow-500 text-sm">
              â³ {participantsState[1]?.name} has reported: {team2Reported?.opponentScore} - {team2Reported?.score}
            </Typography>
            <Typography className="text-yellow-400 text-xs mt-1">
              Waiting for {participantsState[0]?.name} to confirm or admin to verify
            </Typography>
          </div>
        )
      case 'both_reported':
        return (
          <div className="bg-blue-500/20 border border-blue-500 rounded-lg p-3 mb-4">
            <Typography className="text-blue-500 text-sm">
              âœ… Both teams have reported - checking for match...
            </Typography>
          </div>
        )
      case 'disputed':
        return (
          <div className="bg-red-500/20 border border-red-500 rounded-lg p-3 mb-4">
            <Typography className="text-red-500 text-sm">
              âš ï¸ DISPUTED - Scores don't match!
            </Typography>
            <Typography className="text-red-400 text-xs mt-1">
              {participantsState[0]?.name}: {team1Reported?.score} - {team1Reported?.opponentScore}
            </Typography>
            <Typography className="text-red-400 text-xs">
              {participantsState[1]?.name}: {team2Reported?.opponentScore} - {team2Reported?.score}
            </Typography>
            <Typography className="text-red-400 text-xs mt-1">
              Admin must resolve this dispute
            </Typography>
          </div>
        )
      case 'confirmed':
        return (
          <div className="bg-green/20 border border-green rounded-lg p-3 mb-4">
            <Typography className="text-green text-sm">
              âœ… Match result confirmed
            </Typography>
          </div>
        )
      default:
        return null
    }
  }

  // Render captain submission form
  const renderCaptainForm = () => {
    // Check if this captain already reported
    const alreadyReported = (userTeamInfo?.teamIndex === 0 && team1Reported) ||
                           (userTeamInfo?.teamIndex === 1 && team2Reported)

    if (alreadyReported) {
      const myReport = userTeamInfo?.teamIndex === 0 ? team1Reported : team2Reported
      return (
        <div className="bg-dark-input rounded-lg p-4">
          <Typography className="text-green mb-2">âœ… You already submitted your score</Typography>
          <Typography className="text-dark-typo-gray">
            Your score: {myReport?.score} - {myReport?.opponentScore}
          </Typography>
          <Typography className="text-dark-typo-gray text-sm mt-2">
            Waiting for opponent confirmation or admin verification
          </Typography>
        </div>
      )
    }

    return (
      <div className="space-y-4">
        <Typography className="text-dark-typo-gray text-sm mb-2">
          Enter the match score as you saw it:
        </Typography>

        {/* My Team Score */}
        <div className="bg-dark-input rounded-lg p-4">
          <Typography className="text-green font-semibold mb-2">
            Your Team: {userTeamInfo?.teamName}
          </Typography>
          <Input
            type="number"
            placeholder="Your score"
            value={myTeamScore || ''}
            onChange={(e) => setMyTeamScore(parseInt(e.target.value) || 0)}
            className="w-full"
          />
        </div>

        {/* Opponent Score */}
        <div className="bg-dark-input rounded-lg p-4">
          <Typography className="text-red-400 font-semibold mb-2">
            Opponent: {userTeamInfo?.opponentName}
          </Typography>
          <Input
            type="number"
            placeholder="Opponent score"
            value={opponentScore || ''}
            onChange={(e) => setOpponentScore(parseInt(e.target.value) || 0)}
            className="w-full"
          />
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button onClick={reset} variant="tint">
            Reset
          </Button>
          <Button onClick={saveAsCaptain} loading={pending} wide>
            Submit Score
          </Button>
        </div>
      </div>
    )
  }

  // Render admin form (can set both scores directly)
  const renderAdminForm = () => (
    <>
      {participants?.map((p) => (
        <span
          key={p.teamId}
          className={`flex flex-col p-4 border-4 border-${
            p.isWinner ? 'primary' : 'secondary'
          } rounded-[0.8em]`}
        >
          <span className="flex justify-between">
            <Typography as="h4" variant="h4">
              {p.name}
            </Typography>
            <Typography
              className={`text-${p.isWinner ? 'green-100' : 'red-100'}`}
            >
              {p.isWinner ? 'Winner' : ''}
            </Typography>
          </span>
          <span className="flex justify-between items-end">
            <span className="flex-1 flex-col gap-4">
              <label htmlFor={`score-input-${p.teamId}`}>
                <Typography as="h6">Score</Typography>
              </label>
              <Input
                id={`score-input-${p.teamId}`}
                type="number"
                stretch
                value={p.score}
                border="full"
                onChange={(e) =>
                  changeScore(
                    p.teamId,
                    e.target.value ? Number.parseInt(e.target.value) : 0,
                  )
                }
                onBlur={validateWinner}
              />
            </span>
          </span>
          <button className="stack gap-2 self-start mt-2">
            {p.screenshots?.map((ss) => (
              <img
                alt={ss._id}
                key={ss._id}
                src={ss.secure_url}
                loading="lazy"
                className="w-24 h-24 rounded"
                onClick={() => {
                  setImages(p.screenshots?.map((ss) => ss.secure_url) || [])
                  setCurrentImageIndex(
                    p.screenshots?.findIndex((s) => s._id === ss._id) || 0,
                  )
                }}
              />
            ))}
          </button>
        </span>
      ))}

      <span className="flex justify-end gap-2">
        <Button onClick={reset} variant="tint">
          Reset
        </Button>
        <Button onClick={saveAsAdmin} loading={pending} wide>
          Confirm Result
        </Button>
      </span>
    </>
  )

  return (
    <Modal
      isOpened={isOpen}
      title={`Round ${roundIndex + 1} Match ${matchIndex + 1}`}
      onClose={() => closeMatchWinnerModal()}
      withCard={true}
    >
      <Box col gap={4}>
        {/* Show confirmation status if any */}
        {getConfirmationStatusDisplay()}

        {/* Role indicator */}
        <div className="text-xs text-dark-typo-gray mb-2">
          {canDirectlyConfirm ? (
            <span className="text-green">ðŸ”‘ Admin/Organizer - Can directly confirm results</span>
          ) : isCaptain ? (
            <span className="text-yellow-500">ðŸ‘¤ Team Captain - Submit your score for confirmation</span>
          ) : (
            <span className="text-red-400">âš ï¸ You are not authorized to submit scores for this match</span>
          )}
        </div>

        {/* Render appropriate form based on role */}
        {canDirectlyConfirm ? (
          renderAdminForm()
        ) : isCaptain ? (
          renderCaptainForm()
        ) : (
          <div className="text-center py-4">
            <Typography className="text-dark-typo-gray">
              Only team captains or admins can submit match results.
            </Typography>
          </div>
        )}
      </Box>

      {/* Screenshot Modal */}
      <Modal
        isOpened={images.length > 0}
        title="Screenshots"
        onClose={() => setImages([])}
        withCard={true}
      >
        <Box row gap={4}>
          <img
            alt="screenshot"
            src={images[currentImageIndex]}
            loading="lazy"
            className="w-96 h-96 rounded"
            onClick={() => {}}
          />
          <span className="flex flex-col gap-4">
            <Button
              onClick={() =>
                setCurrentImageIndex(
                  (currentImageIndex - 1 + images.length) % images.length,
                )
              }
              variant="tint"
            >
              Previous
            </Button>
            <Button
              onClick={() =>
                setCurrentImageIndex((currentImageIndex + 1) % images.length)
              }
              variant="tint"
            >
              Next
            </Button>
          </span>
        </Box>
      </Modal>
    </Modal>
  )
}
